import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ModalController } from '@ionic/angular';
import { MultipleFoundersInfoModalComponent } from '../../modals/multiple-founders-info-modal/multiple-founders-info-modal.component';
import { FormGroup } from '@angular/forms';
import { environment } from '@shared-envs';

interface Founder {
  userId: number;
  nickname: string;
  percentage?: number; // always stored as decimal (0.49)
  inputError?: string;
  icon?: string;
  nft?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  userIndexDetails?: {
    userIndex: number;
    userIndexDiff: number;
    userIndexAmbassador: number;
    userIndexCreator: number;
    userIndexHelper: number;
  };
  status?: string;
}

@Component({
  selector: 'ark-lib-ownership-block',
  templateUrl: './ownership-block.component.html',
  styleUrls: ['./ownership-block.component.css'],
})
export class OwnershipBlockComponent implements OnChanges {
  @Input() founderData!: Founder;
  @Input() selectedFounders: Founder[] = [];
  @Input() selectedOwnership: 'single' | 'multiple' | null = null;
  @Input() readOnly: boolean = false;
  @Input() createService: any;
  @Output() founderAdded = new EventEmitter<void>();
  @Output() founderRemoved = new EventEmitter<Founder>();

  @Input() foundersForm!: FormGroup;

  founderPercentage: number = 100; // always in percent (0-100)

  options = {
    name: 'founders',
    items: [
      { label: 'Single Founder', value: 'single', checked: false },
      { label: 'Multiple Founders', value: 'multiple', checked: false },
    ],
  };

  constructor(
    private modalController: ModalController,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedOwnership']) {
      if (this.selectedOwnership === 'single') {
        this.founderPercentage = 100;
      }
    }
    // Convert all founder percentages from decimal to percent for display
    if (changes['selectedFounders'] && this.selectedFounders) {
      this.selectedFounders.forEach(f => {
        if (f.percentage !== undefined && f.percentage <= 1) {
          f.percentage = +(f.percentage * 100).toFixed(2);
        }
        if (!f.status) {
          f.status = 'invited';
        }
      });
    }
    if (changes['selectedFounders'] && !changes['selectedFounders'].firstChange) {
      environment.log('selectedFounders changed:', this.selectedFounders);
      this.recalculatePercentages();
      this.updateForm();
    }
  }

  onOwnershipChange(value: 'single' | 'multiple') {
    this.selectedOwnership = value;
    if (value === 'single') {
      this.selectedFounders = [];
      this.founderPercentage = 100;
    }
    this.recalculatePercentages();
    this.updateForm();
  }

  openOwnershipModal(item: any) {
    if (item?.value !== 'multiple') {
      return;
    }
    this.modalController
      .create({
        component: MultipleFoundersInfoModalComponent,
        initialBreakpoint: 1,
        breakpoints: [1],
        cssClass: 'h-full',
      })
      .then((modal) => modal.present());
  }

  recalculatePercentages() {
    // All percentages are in percent (0-100) for display
    const totalPeople = 1 + (this.selectedFounders?.length || 0);
    if (totalPeople === 0) {
      this.founderPercentage = 100;
      return;
    }
    // Find founders that need default values
    const foundersNeedingValues = this.selectedFounders.filter(
      (cf) => cf.percentage === undefined || cf.percentage === null
    );
    // Sum of founders that already have values
    const sumOfExistingFounders = this.selectedFounders
      .filter((cf) => cf.percentage !== undefined && cf.percentage !== null)
      .reduce((sum, cf) => sum + cf.percentage!, 0);
    // Reserve at least 1% for the original founder
    const availableForNew = Math.max(0, 99 - sumOfExistingFounders);
    // Distribute available space evenly between new founders and creator
    const shareForNew = foundersNeedingValues.length > 0
      ? Math.floor(availableForNew / (foundersNeedingValues.length + 1))
      : 0;
    foundersNeedingValues.forEach((cf) => {
      cf.percentage = shareForNew;
      cf.inputError = shareForNew < 1 ? 'Percentage must be 1-99' : '';
    });
    // Recalculate total
    const sumOfFounders = this.selectedFounders.reduce(
      (sum, cf) => sum + (cf.percentage || 0),
      0,
    );
    // Founder gets the rest, never below 1%
    this.founderPercentage = Math.max(1, +(100 - sumOfFounders).toFixed(2));
    this.updateForm();
  }

  updateFounderPercentage(founder: Founder, newPercentage: any) {
    if (!founder || !this.selectedFounders) return;
    const parsed = +Number(newPercentage);
    if (isNaN(parsed)) {
      founder.inputError = 'Please enter a valid number';
      return;
    }
    if (parsed < 1 || parsed > 99) {
      founder.inputError = 'Percentage must be 1-99';
      return;
    }
    const otherTotal = this.selectedFounders
      .filter((cf) => cf.userId !== founder.userId)
      .reduce((sum, cf) => sum + (cf.percentage || 0), 0);
    if (otherTotal + parsed > 99) {
      founder.inputError = 'Total would exceed 99% — founder must keep at least 1%';
      return;
    }
    founder.inputError = '';
    founder.percentage = +parsed.toFixed(2);
    this.founderPercentage = +(100 - otherTotal - parsed).toFixed(2);
    this.updateForm();
  }

  onAddFounderButtonClick() {
    this.founderAdded.emit();
  }

  removeFounder(founder: Founder) {
    this.selectedFounders = this.selectedFounders.filter(
      (c) => c.userId !== founder.userId,
    );
    this.recalculatePercentages();
    this.founderRemoved.emit(founder);
  }

  private updateForm() {
    if (!this.foundersForm) return;
    const hasOutOfRangeFounder = this.selectedFounders.some(
      (cf) => (cf.percentage ?? 0) < 1 || (cf.percentage ?? 0) > 99,
    );
    if (hasOutOfRangeFounder) {
      this.createService.validations['founders'].error =
        'Each founder percentage must be between 1 and 99';
      return;
    }

    const selectedFoundersTotal = this.selectedFounders.reduce(
      (sum, cf) => sum + (cf.percentage || 0),
      0,
    );
    if (selectedFoundersTotal > 99) {
      this.createService.validations['founders'].error =
        'Total selected founder percentage cannot exceed 99';
      return;
    }

    const foundersFormValue = [
      ...this.selectedFounders.map((cf) => ({
        userId: cf.userId,
        percentage: +(cf.percentage! / 100).toFixed(4),
      })),
      {
        userId: this.founderData.userId,
        percentage: +(this.founderPercentage / 100).toFixed(4),
      },
    ];
    this.foundersForm.get('founders')?.setValue(foundersFormValue);
    this.foundersForm.get('founders')?.markAsDirty();
    this.foundersForm
      .get('ownershipHasMultipleFounders')
      ?.setValue(this.selectedOwnership === 'multiple');
    if (this.createService.validations['founders']?.error) {
      this.createService.validations['founders'].error = '';
    }
  }
}
